import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { IonIcon } from '@ionic/react';
import { cubeOutline } from 'ionicons/icons';
import BrowserWindow from 'components/atoms/BrowserWindow';
import { projectWorkflowMarketplacePath } from '../../../utils/marketplacePaths';
import './TestRuns.css';

const STEPS = [
  {
    command: 'npm install -g @zibby/cli',
    output: [
      { text: 'Resolving dependencies...', cls: '' },
      { text: 'added 142 packages in 8s', cls: '' },
      { text: '✓ @zibby/cli installed', cls: 'gss-success' },
    ],
  },
  {
    command: 'zibby workflow new my-pipeline',
    output: [
      { text: '⚙ Scaffolding .zibby/workflows/my-pipeline/...', cls: '' },
      { text: '  graph.mjs        — plan(claude) → implement(cursor) → verify(codex)', cls: '' },
      { text: '  workflow.json    — manifest', cls: '' },
      { text: '  nodes/           — 3 starter nodes with Zod schemas', cls: '' },
      { text: '✓ Workflow scaffolded', cls: 'gss-success' },
    ],
  },
  {
    command: 'zibby workflow start my-pipeline',
    output: [
      { text: '▶ Running my-pipeline locally…', cls: '' },
      { text: '  ┌ plan       (claude)  → PlanSchema   ✓ (3.1s)', cls: 'gss-success' },
      { text: '  ├ implement  (cursor)  → DiffSchema   ✓ (12.4s)', cls: 'gss-success' },
      { text: '  └ verify     (codex)   → ResultSchema ✓ (5.7s)', cls: 'gss-success' },
    ],
  },
  {
    command: 'zibby workflow deploy my-pipeline',
    output: [
      { text: 'Building Heroku-style bundle…', cls: '' },
      { text: '⬆ Uploading to Zibby Cloud…', cls: '' },
      { text: '✓ Deployed (uuid: 8a3f9e2c-bc11-4b6e-a8e3-2f9c7d5a0e91)', cls: 'gss-success' },
      { text: '→ Trigger from CI/CD:  zibby workflow trigger 8a3f9e2c -t', cls: 'gss-link-line' },
    ],
  },
];

const CHAR_SPEED = 18;
const OUTPUT_LINE_DELAY = 120;
const STEP_PAUSE = 300;

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const WorkflowDemo = () => {
  // Component renders inside ProjectLayout — projectId is in the URL.
  // Used to deep-link the "marketplace template" CTA into the current
  // project's marketplace (no project context = link gets disabled).
  const { projectId } = useParams();
  const [visibleSteps, setVisibleSteps] = useState([]);
  const cancelRef = useRef(false);
  const timeoutsRef = useRef([]);

  const clearAllTimeouts = useCallback(() => {
    cancelRef.current = true;
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, []);

  const delay = useCallback((ms) => {
    return new Promise((resolve) => {
      const t = setTimeout(() => {
        if (!cancelRef.current) resolve();
      }, ms);
      timeoutsRef.current.push(t);
    });
  }, []);

  useEffect(() => {
    cancelRef.current = false;

    const animate = async () => {
      // Wait for the wrapper's CSS fade-in (320ms) to finish before typing
      // — otherwise the first command appears mid-fade and the toggle feels
      // like nothing changed.
      await delay(380);
      for (let si = 0; si < STEPS.length; si++) {
        if (cancelRef.current) return;
        const step = STEPS[si];

        for (let ci = 1; ci <= step.command.length; ci++) {
          if (cancelRef.current) return;
          setVisibleSteps(prev => {
            const next = [...prev];
            next[si] = { commandText: step.command.slice(0, ci), outputLines: [], done: false };
            return next;
          });
          await delay(CHAR_SPEED);
        }

        for (let oi = 0; oi < step.output.length; oi++) {
          if (cancelRef.current) return;
          await delay(OUTPUT_LINE_DELAY);
          setVisibleSteps(prev => {
            const next = [...prev];
            const current = { ...next[si] };
            current.outputLines = [...current.outputLines, step.output[oi]];
            next[si] = current;
            return next;
          });
        }

        setVisibleSteps(prev => {
          const next = [...prev];
          next[si] = { ...next[si], done: true };
          return next;
        });

        if (si < STEPS.length - 1) {
          await delay(STEP_PAUSE);
        }
      }
    };

    animate();
    return clearAllTimeouts;
  }, [delay, clearAllTimeouts]);

  return (
    <div className="gss-wrapper">
      <div className="gss-header">
        <IonIcon icon={cubeOutline} className="gss-header-icon" />
        <span className="gss-header-text">
          Compose and deploy a multi-agent pipeline in 4 steps — or start from a{' '}
          {/* This component renders in two places: the public homepage
              (no projectId in the URL) and inside ProjectLayout (projectId
              present). Route the link to the right scope automatically —
              never falls back to a disabled span. */}
          <Link
            className="gss-header-link"
            to={projectId ? projectWorkflowMarketplacePath(projectId) : '/marketplace'}
          >
            marketplace template
          </Link>.
        </span>
      </div>
      <BrowserWindow variant="dark" className="gss-terminal">
        <div className="gss-terminal-body">
          {STEPS.map((step, si) => {
            const vis = visibleSteps[si];
            if (!vis) return null;
            return (
              <div key={si} className="gss-step">
                <div className="gss-command">
                  <span className="gss-prompt">$</span>
                  <span className="gss-command-text">
                    {vis.commandText}
                    {!vis.done && vis.outputLines.length === 0 && <span className="gss-cursor">|</span>}
                  </span>
                  {vis.done && (
                    <button className="gss-copy-btn" onClick={() => navigator.clipboard?.writeText(step.command)} aria-label="Copy command">
                      <CopyIcon />
                    </button>
                  )}
                </div>
                {vis.outputLines.length > 0 && (
                  <div className="gss-output">
                    {vis.outputLines.map((line, li) => (
                      <span key={li} className={`gss-output-line gss-indent ${line.cls}`}>
                        {line.cls === 'gss-link-line'
                          ? <>{line.text}</>
                          : line.text
                        }
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </BrowserWindow>
    </div>
  );
};

export default WorkflowDemo;
